import { useEffect, useRef } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DetailPanel, useDetailPanelInteractionValue, type PanelSpan } from '@/components/shared';
import type { BackofficeMessageBusPeekedMessage } from '../types';

const SELECTED_MESSAGE_KEY = 'messagebus:selected-message';

export interface MessageBusMessagesTableTileProps {
  title: string;
  topicName: string;
  subscriberName: string;
  sourceLabel: string;
  messages: BackofficeMessageBusPeekedMessage[];
  emptyLabel: string;
  span?: PanelSpan;
  currentSpan?: PanelSpan;
  panelId?: string;
  selectedMessageKeys: ReadonlySet<string>;
  onSelectedMessageKeysChange: (messageKeys: Set<string>) => void;
  onManageSelected: () => void;
}

function formatTimestamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export function MessageBusMessagesTableTile({
  title,
  topicName,
  subscriberName,
  sourceLabel,
  messages,
  emptyLabel,
  span = '4x3',
  currentSpan,
  panelId,
  selectedMessageKeys,
  onSelectedMessageKeysChange,
  onManageSelected,
}: MessageBusMessagesTableTileProps) {
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const [selectedMessage, setSelectedMessage] =
    useDetailPanelInteractionValue<BackofficeMessageBusPeekedMessage>(SELECTED_MESSAGE_KEY);
  const allMessagesSelected =
    messages.length > 0 && messages.every((message) => selectedMessageKeys.has(message.messageKey));
  const someMessagesSelected = messages.some((message) =>
    selectedMessageKeys.has(message.messageKey)
  );

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someMessagesSelected && !allMessagesSelected;
    }
  }, [allMessagesSelected, someMessagesSelected]);

  useEffect(() => {
    if (messages.length === 0) {
      setSelectedMessage(undefined);
      return;
    }

    const nextSelectedMessage = selectedMessage
      ? messages.find((message) => message.messageKey === selectedMessage.messageKey)
      : null;

    setSelectedMessage(nextSelectedMessage ?? messages[0]);
  }, [messages, selectedMessage, setSelectedMessage]);

  return (
    <DetailPanel
      title={title}
      span={span}
      currentSpan={currentSpan}
      panelId={panelId}
      contentOverflow="auto"
    >
      {messages.length === 0 ? (
        <div className="py-10 text-center text-body text-neutral-500">{emptyLabel}</div>
      ) : (
        <div className="min-w-[980px] overflow-auto" data-testid="messagebus-messages-table-tile">
          <div className="mb-3 flex items-center justify-between gap-3 text-xs text-neutral-500">
            <span>{sourceLabel}</span>
            <div className="flex items-center gap-3">
              <span className="tabular-nums">{messages.length} messages</span>
              <button
                type="button"
                disabled={selectedMessageKeys.size === 0}
                onClick={onManageSelected}
                className="focus-ring inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-backoffice-500 bg-backoffice-500 px-3 text-small font-medium text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-400"
              >
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                Manage selected ({selectedMessageKeys.size})
              </button>
            </div>
          </div>
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-[11px] uppercase tracking-[0.08em] text-neutral-500">
                <th className="w-12 px-3 py-2 font-semibold">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allMessagesSelected}
                    aria-label="Select all loaded dead-letter messages"
                    className="h-4 w-4 rounded border-neutral-300 text-backoffice-600"
                    onChange={(event) => {
                      onSelectedMessageKeysChange(
                        event.target.checked
                          ? new Set(messages.map((message) => message.messageKey))
                          : new Set()
                      );
                    }}
                  />
                </th>
                <th className="px-3 py-2 font-semibold">Message</th>
                <th className="px-3 py-2 font-semibold">Subject</th>
                <th className="px-3 py-2 font-semibold">Enqueued</th>
                <th className="px-3 py-2 font-semibold">Content type</th>
                <th className="px-3 py-2 font-semibold">Delivery</th>
                <th className="px-3 py-2 font-semibold">Dead-letter reason</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => {
                const isSelected = selectedMessage?.messageKey === message.messageKey;
                const isChecked = selectedMessageKeys.has(message.messageKey);
                const messageLabel =
                  message.messageId ?? message.sequenceNumber ?? message.messageKey;
                return (
                  <tr
                    key={message.messageKey}
                    className={classNames(
                      'cursor-pointer border-b border-neutral-100 align-top hover:bg-neutral-50',
                      (isSelected || isChecked) && 'bg-backoffice-50'
                    )}
                    onClick={() => setSelectedMessage(message)}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        aria-label={`Select message ${messageLabel}`}
                        className="h-4 w-4 rounded border-neutral-300 text-backoffice-600"
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          const nextMessageKeys = new Set(selectedMessageKeys);
                          if (event.target.checked) {
                            nextMessageKeys.add(message.messageKey);
                          } else {
                            nextMessageKeys.delete(message.messageKey);
                          }
                          onSelectedMessageKeysChange(nextMessageKeys);
                        }}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-neutral-900">{messageLabel}</div>
                      <div className="mt-1 text-xs text-neutral-500">
                        <Link
                          to={`/developer/messagebus/topics/${encodeURIComponent(topicName)}`}
                          className="hover:text-backoffice-600"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {topicName}
                        </Link>
                        {' / '}
                        <Link
                          to={`/developer/messagebus/topics/${encodeURIComponent(topicName)}/subscribers/${encodeURIComponent(subscriberName)}`}
                          className="hover:text-backoffice-600"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {subscriberName}
                        </Link>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-neutral-700">{message.subject ?? '—'}</td>
                    <td className="px-3 py-3 text-neutral-700">
                      {formatTimestamp(message.enqueuedAt)}
                    </td>
                    <td className="px-3 py-3 text-neutral-700">{message.contentType ?? '—'}</td>
                    <td className="px-3 py-3 text-neutral-700">
                      {message.deliveryCount === null ? '—' : String(message.deliveryCount)}
                    </td>
                    <td className="px-3 py-3 text-neutral-700">
                      {message.deadLetterReason ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DetailPanel>
  );
}
